import { makeObservable, observable, action } from "mobx";

import { setLastViewed as setLastViewedMutation } from "@framework/notifications/graphql/set-last-viewed";
import { UserWithProfilesFragment } from "@framework/notifications/graphql/types";
import { makeRequest } from "@framework/notifications/request";
import { IdentityUser } from "@voxmedia/duet-context/context/auth-context";
import {
  ViewerUserWithToken,
  User,
  UserStore as UserStoreType,
} from "@voxmedia/duet-context/context/notifications-context";

const query = `
  query ViewerQuery {
    viewer {
      id
      lastViewed
      inPageNotificationSettings {
        enabled
        onCommentReply
        onPostReply
        onModeration
        onRec
      }
      ${UserWithProfilesFragment}
    }
  }
`;

interface ViewerResponse {
  viewer?: User;
}

class UserStore implements UserStoreType {
  public user: IdentityUser;
  public viewer: ViewerUserWithToken | null;
  public viewerLoading: boolean;
  public constructor(user: IdentityUser) {
    this.user = user;
    this.viewer = null;
    this.viewerLoading = true;
    makeObservable(this, {
      user: observable,
      viewer: observable,
      viewerLoading: observable,
      setUser: action,
      setViewer: action,
      loadViewer: action,
    });
  }

  public setUser(user: IdentityUser) {
    this.user = user;
  }

  public setViewer(viewer: ViewerUserWithToken | null) {
    this.viewer = viewer;
  }

  public async loadViewer() {
    if (!this.user.notificationsToken) {
      this.setViewer(null);
      this.viewerLoading = false;
      return;
    }

    const serverURL = process.env.NEXT_PUBLIC_NOTIFICATIONS_SERVICE_URL;
    if (!serverURL) {
      this.setViewer(null);
      this.viewerLoading = false;
      return;
    }

    const url = new URL("/api", serverURL);
    const response = await makeRequest<ViewerResponse>(
      url.toString(),
      query,
      {},
      this.user.notificationsToken,
    );

    if (!response.data || !response.data.viewer || response.errors) {
      this.setViewer(null);
      this.viewerLoading = false;
      return;
    }

    const viewer: ViewerUserWithToken = {
      ...response.data.viewer,
      token: this.user.notificationsToken,
    };

    this.setViewer(viewer);
    this.viewerLoading = false;
  }

  public async setLastViewed(lastViewed: Date) {
    if (!this.viewer?.token) {
      return;
    }

    await setLastViewedMutation({
      token: this.viewer.token,
      value: lastViewed,
    });

    this.setViewer({
      ...this.viewer,
      lastViewed,
    });
  }
}

export default UserStore;
